import '../../styles/date-input.scss';

import { ChangeEvent, forwardRef, HTMLInputAutoCompleteAttribute, ReactNode, useState } from 'react';

import Fieldset, { Legend } from './Fieldset';
import FormGroup from './FormGroup';

type DateInputProps = {
  name: string;
  label?: string;
  errorLabel?: string;
  legendSize?: 'l' | 'm' | 's';
  hint?: ReactNode;
  value: Date | undefined;
  onChange: (event: Date | ChangeEvent<Element> | undefined) => void;
  error?: string;
  isOptional?: boolean;
  includeTime?: boolean;
  timeZoneLabel?: string;
  autoComplete?: HTMLInputAutoCompleteAttribute | undefined;
};

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      name,
      label,
      errorLabel = label,
      legendSize,
      hint,
      value,
      onChange,
      error,
      isOptional = false,
      includeTime = false,
      timeZoneLabel = 'UTC',
      autoComplete,
    }: DateInputProps,
    ref,
  ) => {
    const [day, setDay] = useState<number | undefined>(value ? value.getDate() : undefined);
    const [month, setMonth] = useState<number | undefined>(value ? value.getMonth() + 1 : undefined);
    const [year, setYear] = useState<number | undefined>(value ? value.getFullYear() : undefined);
    const [time, setTime] = useState<string | undefined>(() => {
      if (value instanceof Date) {
        const hours = value.getHours().toString().padStart(2, '0');
        const minutes = value.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
      }
      return undefined;
    });

    const isValidDay = () => {
      return day !== undefined && day >= 1 && day <= 31;
    };
    const isValidMonth = () => {
      return month !== undefined && month >= 1 && month <= 12;
    };
    const isValidYear = () => {
      return year !== undefined && year.toString().length == 4;
    };
    const isValidTimeFormat = () => {
      return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time || '');
    };

    const handleChange = () => {
      const isValidDate = isValidDay() && isValidMonth() && isValidYear();
      const isValidTime = isValidTimeFormat();

      if (isValidDate && (!includeTime || isValidTime)) {
        const selectedDate = !includeTime
          ? new Date(year!, month! - 1, day!)
          : new Date(year!, month! - 1, day!, parseInt(time?.split(':')[0] || ''), parseInt(time?.split(':')[1] || ''));
        onChange(selectedDate);
      } else if (isOptional && (day !== undefined || month !== undefined || year !== undefined)) {
        onChange(new Date('Invalid Date'));
      } else {
        onChange(undefined);
      }
    };

    let dayClass = 'govuk-input govuk-date-input__input govuk-input--width-2';
    let monthClass = 'govuk-input govuk-date-input__input govuk-input--width-2';
    let yearClass = 'govuk-input govuk-date-input__input govuk-input--width-4';
    let timeClass = 'govuk-input govuk-input--width-10';

    let subError = '';
    if (error) {
      const missingFields = [];
      let invalidDate = false;
      let invalidYearLength = false;
      let invalidTime = false;
      if (!isValidDay()) {
        if (day == undefined) {
          missingFields.push('day');
        } else {
          invalidDate = true;
        }
        dayClass += ' govuk-input--error';
      }
      if (!isValidMonth()) {
        if (month == undefined) {
          missingFields.push('month');
        } else {
          invalidDate = true;
        }
        monthClass += ' govuk-input--error';
      }
      if (!isValidYear()) {
        if (year == undefined) {
          missingFields.push('year');
        } else if (year.toString().length != 4) {
          invalidYearLength = true;
        }
        yearClass += ' govuk-input--error';
      }
      if (includeTime && !isValidTimeFormat()) {
        if (time == undefined || time == '') {
          missingFields.push('time');
        } else {
          invalidTime = true;
        }
        timeClass += ' govuk-input--error';
      }
      if (invalidDate) {
        subError = `${errorLabel} must be a real date.`;
      } else if (missingFields.length == 1) {
        subError = `${errorLabel} must include a ${missingFields[0]}.`;
      } else if (missingFields.length == 2) {
        subError = `${errorLabel} must include a ${missingFields[0]} and ${missingFields[1]}.`;
      } else if (invalidYearLength) {
        subError = `Year must include 4 numbers.`;
      } else if (includeTime && invalidTime) {
        subError = `Time must be in the format HH:MM AM/PM.`;
      }
    }

    return (
      <FormGroup error={!!error}>
        <Fieldset>
          {label && <Legend size={legendSize}>{label}</Legend>}
          {hint && (
            <div id={`${name}-hint`} className="govuk-hint">
              {hint}
            </div>
          )}
          {error && (
            <p id={`${name}-error`} className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span> {error}
            </p>
          )}
          {subError && (
            <p className="govuk-error-message sub-error">
              <span className="govuk-visually-hidden">Error:</span> {subError}
            </p>
          )}
          <div className="govuk-date-input" id={name}>
            <div className="govuk-date-input__item">
              <div className="govuk-form-group">
                <label className="govuk-label govuk-date-input__label" htmlFor={`${name}.day`}>
                  Day
                </label>
                <input
                  type="text"
                  id={`${name}.day`}
                  name={`${name}.day`}
                  className={dayClass}
                  value={day !== undefined && !isNaN(day) ? day : ''}
                  onChange={(e) => (e.target.value ? setDay(parseInt(e.target.value, 10)) : setDay(undefined))}
                  onBlur={handleChange}
                  onKeyUp={handleChange}
                  inputMode="numeric"
                  min="1"
                  max="31"
                  autoComplete={autoComplete}
                  aria-autocomplete={autoComplete === 'off' ? 'none' : undefined}
                  aria-haspopup={autoComplete === 'off' ? 'false' : undefined}
                  ref={ref}
                />
              </div>
            </div>

            <div className="govuk-date-input__item">
              <div className="govuk-form-group">
                <label className="govuk-label govuk-date-input__label" htmlFor={`${name}.month`}>
                  Month
                </label>
                <input
                  type="text"
                  id={`${name}.month`}
                  name={`${name}.month`}
                  className={monthClass}
                  value={month !== undefined && !isNaN(month) ? month : ''}
                  onChange={(e) => (e.target.value ? setMonth(parseInt(e.target.value, 10)) : setMonth(undefined))}
                  onBlur={handleChange}
                  onKeyUp={handleChange}
                  inputMode="numeric"
                  min="1"
                  max="12"
                  autoComplete={autoComplete}
                  aria-autocomplete={autoComplete === 'off' ? 'none' : undefined}
                  aria-haspopup={autoComplete === 'off' ? 'false' : undefined}
                  ref={ref}
                />
              </div>
            </div>

            <div className="govuk-date-input__item">
              <div className="govuk-form-group">
                <label className="govuk-label govuk-date-input__label" htmlFor={`${name}.year`}>
                  Year
                </label>
                <input
                  type="text"
                  id={`${name}.year`}
                  name={`${name}.year`}
                  className={yearClass}
                  value={year !== undefined && !isNaN(year) ? year : ''}
                  onChange={(e) => (e.target.value ? setYear(parseInt(e.target.value, 10)) : setYear(undefined))}
                  onBlur={handleChange}
                  onKeyUp={handleChange}
                  inputMode="numeric"
                  min="1900"
                  max="2099"
                  autoComplete={autoComplete}
                  aria-autocomplete={autoComplete === 'off' ? 'none' : undefined}
                  aria-haspopup={autoComplete === 'off' ? 'false' : undefined}
                  ref={ref}
                />
              </div>
            </div>

            {includeTime && (
              <div className="govuk-date-input__item">
                <div className="govuk-form-group">
                  <label className="govuk-label govuk-date-input__label" htmlFor={`${name}.time`}>
                    {`Time (${timeZoneLabel})`}
                  </label>
                  <input
                    type="time"
                    id={`${name}.time`}
                    name={`${name}.time`}
                    className={timeClass}
                    value={time || ''}
                    onChange={(e) => setTime(e.target.value)}
                    onBlur={handleChange}
                    onKeyUp={handleChange}
                    autoComplete={autoComplete}
                    aria-autocomplete={autoComplete === 'off' ? 'none' : undefined}
                    aria-haspopup={autoComplete === 'off' ? 'false' : undefined}
                    ref={ref}
                  />
                </div>
              </div>
            )}

            {error && <span>{error}</span>}
          </div>
        </Fieldset>
      </FormGroup>
    );
  },
);

DateInput.displayName = 'DateInput';

export default DateInput;
